import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt,
  faPlus,
  faFilter,
  faSort,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { 
  getLocations, 
  createLocation, 
  updateLocation, 
  deleteLocation, 
  toggleLocationStatus 
} from '../../services/locationService';
import LocationForm from './LocationForm';
import LocationsList from './LocationsList';
import LocationSeeder from './LocationSeeder';
import OrderingControls from './OrderingControls';

const LocationsSection = () => {
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all', // all, active, inactive
    state: 'all',
    search: ''
  });
  const [sortBy, setSortBy] = useState('city-asc');

  // Fetch locations on component mount and after seeding
  useEffect(() => {
    fetchLocations();
  }, []);

  // Refresh locations when seeder component is used
  const handleRefreshLocations = () => {
    fetchLocations();
  };

  // Apply filters and sorting when data or filters change
  useEffect(() => {
    const filterAndSortLocations = () => {
      let filtered = [...locations];

      // Apply status filter
      if (filters.status !== 'all') {
        filtered = filtered.filter(location => 
          filters.status === 'active' ? location.isActive : !location.isActive
        );
      }

      // Apply state filter
      if (filters.state !== 'all') {
        filtered = filtered.filter(location => location.state === filters.state);
      }

      // Apply search filter
      if (filters.search.trim()) {
        const searchTerm = filters.search.toLowerCase().trim();
        filtered = filtered.filter(location =>
          location.city.toLowerCase().includes(searchTerm) ||
          location.state.toLowerCase().includes(searchTerm) ||
          location.zipCodes?.some(zip => zip.includes(searchTerm)) ||
          location.notes?.toLowerCase().includes(searchTerm)
        );
      }

      // Apply sorting
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'city-asc':
            return a.city.localeCompare(b.city);
          case 'city-desc':
            return b.city.localeCompare(a.city);
          case 'state-asc':
            return a.state.localeCompare(b.state);
          case 'state-desc':
            return b.state.localeCompare(a.state);
          case 'created-asc':
            return new Date(a.createdAt) - new Date(b.createdAt);
          case 'created-desc':
            return new Date(b.createdAt) - new Date(a.createdAt);
          case 'fee-asc':
            return a.deliveryFee - b.deliveryFee;
          case 'fee-desc':
            return b.deliveryFee - a.deliveryFee;
          default:
            return a.city.localeCompare(b.city);
        }
      });

      setFilteredLocations(filtered);
    };

    filterAndSortLocations();
  }, [locations, filters, sortBy]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getLocations();
      setLocations(data);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Failed to load locations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = () => {
    setEditingLocation(null);
    setShowForm(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setShowForm(true);
  };

  const handleSaveLocation = async (locationData) => {
    try {
      if (editingLocation) {
        // Update existing location
        const updatedLocation = await updateLocation(editingLocation.id, locationData);
        setLocations(prev => prev.map(loc => 
          loc.id === editingLocation.id 
            ? { ...loc, ...updatedLocation }
            : loc
        ));
      } else {
        // Create new location
        const newLocation = await createLocation(locationData);
        setLocations(prev => [newLocation, ...prev]);
      }
      
      setShowForm(false);
      setEditingLocation(null);
    } catch (error) {
      console.error('Error saving location:', error);
      setError('Failed to save location. Please try again.');
    }
  };

  const handleDeleteLocation = async (locationId) => {
    try {
      await deleteLocation(locationId);
      setLocations(prev => prev.filter(loc => loc.id !== locationId));
    } catch (error) {
      console.error('Error deleting location:', error);
      setError('Failed to delete location. Please try again.');
    }
  };

  const handleToggleLocationStatus = async (locationId, isActive) => {
    try {
      await toggleLocationStatus(locationId, isActive);
      setLocations(prev => prev.map(loc => 
        loc.id === locationId 
          ? { ...loc, isActive, updatedAt: new Date() }
          : loc
      ));
    } catch (error) {
      console.error('Error toggling location status:', error);
      setError('Failed to update location status. Please try again.');
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingLocation(null);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      state: 'all',
      search: ''
    });
    setSortBy('city-asc');
  };

  const getUniqueStates = () => {
    const states = [...new Set(locations.map(loc => loc.state))];
    return states.sort();
  };

  return (
    <div className="dashboard-section">
      <div className="section-header">
        <h2>
          <FontAwesomeIcon icon={faMapMarkerAlt} />
          Service Locations
        </h2>
        
        <div className="header-actions">
          <button
            onClick={handleAddLocation}
            className="primary-button"
            disabled={showForm}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Location
          </button>
        </div>
      </div>

      {/* Same one-click ordering switch as the dashboard overview — mirrored
          here because this is where you look when a location has a problem. */}
      <OrderingControls />

      {error && (
        <div className="error-message form-error">
          {error}
        </div>
      )}

      {/* Show seeder when no locations exist */}
      {!loading && locations.length === 0 && !showForm && (
        <LocationSeeder onComplete={handleRefreshLocations} />
      )}

      {showForm && (
        <LocationForm
          location={editingLocation}
          onSave={handleSaveLocation}
          onCancel={handleCancelForm}
          isEditing={!!editingLocation}
        />
      )}

      <div className="locations-content">
        <div className="filters-bar">
          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} />
            <input
              type="text"
              placeholder="Search locations..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <div className="filter-box">
            <FontAwesomeIcon icon={faFilter} />
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Locations</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          <div className="filter-box">
            <select
              value={filters.state}
              onChange={(e) => handleFilterChange('state', e.target.value)}
            >
              <option value="all">All States</option>
              {getUniqueStates().map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          <div className="sort-box">
            <FontAwesomeIcon icon={faSort} />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="city-asc">City (A-Z)</option>
              <option value="city-desc">City (Z-A)</option>
              <option value="state-asc">State (A-Z)</option>
              <option value="state-desc">State (Z-A)</option>
              <option value="created-desc">Newest First</option>
              <option value="created-asc">Oldest First</option>
              <option value="fee-asc">Lowest Fee</option>
              <option value="fee-desc">Highest Fee</option>
            </select>
          </div>
          
          <button onClick={clearFilters} className="secondary-button">
            Clear Filters
          </button>
        </div>

        <LocationsList
          locations={filteredLocations}
          onEdit={handleEditLocation}
          onDelete={handleDeleteLocation}
          onToggleStatus={handleToggleLocationStatus}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default LocationsSection; 